/*
 * @Author: MJC
 * @Date: 2023-02-27 15:20:16
 * @LastEditors: MJC
 * @LastEditTime: 2023-03-08 15:12:36
 * @Description: 多选单选整合到底层（此文件）处理
 * @FilePath: /next-ui/packages/wui-table/src/hooks/useSelect.tsx
 */

/* *
1，兼容多选/单选旧有的api
2，逻辑重构实现
*/
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {prefix} from "../../../wui-core/src/index"
import ColumnGroup from '../ColumnGroup';
import Column from '../Column';
import Checkbox from '../../../wui-checkbox/src';
import RadioWrapper from '../../../wui-radio/src';
import Menu from "../../../wui-menu/src";
import Icon from "../../../wui-icon/src";
import Dropdown from "../../../wui-dropdown/src";
import TreeUtils from "../../../wui-core/src/TreeUtils";
import { columnType, defaultDragHandleColumn } from '../constant';
import { TableProps, Key, ColumnType, DefaultRecordType} from '../iTable';
import { MenuSelectionsType } from '../interface'
import { getData, checkAllSelected } from '../lib/util';
import { SelectInfo } from 'rc-menu/lib/interface';
import {
    SELECTION_ALL,
    SELECTION_INVERT,
    SELECTION_NONE
} from '../constant';

const {Item} = Menu;

/**
 *
 * @param rowSelection {rowSelection}
 * @param config {影响因素}
 * return [newColumn, selectRowKeys]
 */
const useSelect = (rowSelection: TableProps['rowSelection'], config: any): [(columns: ColumnType[]) => ColumnType[], any[], (record: DefaultRecordType, index: number, event: React.MouseEvent<HTMLElement>) => void] => {
    // rowSelection 要区分多选和单选的新旧版传参方式，所有需要额外的变量区分多选和单选，不可仅仅根据rowSelection.type区分
    // selectType 由multiSelectX和singleSelect文件内部传递 false | 'checkbox' | 'radio'
    const {
        type: selectionType,
        selectedRowKeys,
        defaultSelectedRowKeys,
        onChange,
        onSelectAll,
        onSelect,
        getCheckboxProps,
        columnTitle,
        columnWidth,
        fixed,
        hideSelectAll,
        renderCell,
        selections,
    } = rowSelection || {};
    const {
        columns,
        data,
        showData,
        selectType,
        getRowKey,
        multiSelectConfig,
        children,
        childrenColumnName,
        getSelectedDataFunc,
        checkStrictly,
        autoSelect,
        autoCheckedByClickRows,
        selectedRowIndex,
        antd
    } = config;

    let [innerSelectedRowKeys, setInnerSelectedRowKeys] = useState<Key[]>([]);
    let [innerDisableKeys, setInnerDisableKeys] = useState<Key[]>([]);
    let ref = useRef({})

    // 实际的单选or多选,false说明都不是
    let realSelectionType = useMemo(() => {
        if (selectType) {
            return selectType
        }
        if (Object.keys(rowSelection || {}).length && selectType !== false) {
            return selectionType || 'checkbox'
        }
        return false
    }, [selectType, selectionType])

    const isOld = useMemo(() => {
        return rowSelection === undefined
    }, [rowSelection])

    const innerAutoSelect = useMemo(() => {
        if (autoSelect !== undefined) {
            return autoSelect
        }
        return !checkStrictly
    }, [checkStrictly, autoSelect])

    let keyTarget = useMemo(() => {
        return getData(data, getRowKey, getCheckboxProps, isOld, selectedRowKeys, defaultSelectedRowKeys, realSelectionType, selectedRowIndex)
    },
    [isOld, selectedRowKeys, defaultSelectedRowKeys, getRowKey, realSelectionType, selectedRowIndex, data]
    )

    const realIsControlled = useMemo(() => {
        if (realSelectionType === 'radio') {
            if (isOld) {
                return selectedRowIndex !== undefined
            } else {
                return false
            }
        } else {
            if (isOld) {
                return false
            } else {
                return selectedRowKeys !== undefined
            }
        }
    }, [isOld, realSelectionType, selectedRowIndex, selectedRowKeys])

    useEffect(() => {
        if (JSON.stringify(keyTarget) !== JSON.stringify(ref.current)) {
            ref.current = keyTarget;
            setInnerSelectedRowKeys(keyTarget._selectedRowKeys)
            setInnerDisableKeys(keyTarget._disableKeys)
        }
    }, [keyTarget])

    const dataTarget = useMemo((): any[] | false => {
        if (!innerAutoSelect) {
            return false
        }
        let target: any[] = [];
        let dataTargetFun = (data: DefaultRecordType[], parentKey = null) => {
            (data || []).forEach((record: DefaultRecordType, index: number) => {
                let currentKey = getRowKey(record, index)
                target.push({
                    key: currentKey,
                    parentKey,
                    checked: innerSelectedRowKeys.includes(currentKey),
                })
                if (record[childrenColumnName] && Array.isArray(record[childrenColumnName]) && record[childrenColumnName].length) {
                    dataTargetFun(record[childrenColumnName], currentKey)
                }
            })
        }
        dataTargetFun(showData)
        return target;
    }, [showData, childrenColumnName, innerAutoSelect, getRowKey, innerSelectedRowKeys])

    const isAllChecked = useMemo(() => {
        const checkAllTarget = checkAllSelected(showData, innerSelectedRowKeys, innerDisableKeys, getRowKey);
        return checkAllTarget;

    }, [showData, innerSelectedRowKeys, innerDisableKeys, getRowKey])

    const onAllCheckChange = useCallback((check: boolean, isAll: boolean = false) => {
        let selectedRowKeyList: Key[] = []; // 选择key集合
        let selectedRowList: DefaultRecordType[] = []; // 选择数据的集合
        let changeRowList: DefaultRecordType[] = []; // 改变选择状态数据的集合
        let handledData = isAll ? [...data] : [...showData]; // 旧版整理后数据的集合
        let handleDataFun = (data: DefaultRecordType[], check: boolean, handledData: DefaultRecordType[]) => {
            data.forEach((item, index) => {
                let currentKey = getRowKey(item, index); // 当前key
                let isDisable = innerDisableKeys.includes(currentKey) // 是否是禁选状态
                // handledData.push({...item})
                if (check) { // 全选
                    if (!isDisable) { // 非禁选
                        selectedRowKeyList.push(currentKey);
                        selectedRowList.push(item);
                        if (!innerSelectedRowKeys.includes(currentKey)) {
                            changeRowList.push(item)
                        }
                        if (isOld) {
                            handledData[index]._checked = true;
                            handledData[index]._disabled = isDisable;
                        }

                    } else { // 禁选
                        if (innerSelectedRowKeys.includes(currentKey)) { // 初始化已选改数据
                            selectedRowKeyList.push(currentKey);
                            selectedRowList.push(item);
                            if (isOld) {
                                handledData[index]._checked = true;
                                handledData[index]._disabled = isDisable;
                            }

                        }
                    }
                } else { // 取消全选
                    if (!isDisable) { // 非禁选
                        if (!innerSelectedRowKeys.includes(currentKey)) {
                            changeRowList.push(item)
                        }
                        if (isOld) {
                            handledData[index]._checked = false;
                            handledData[index]._disabled = isDisable;
                        }

                    } else { // 禁选
                        if (innerSelectedRowKeys.includes(currentKey)) { // 初始化已选改数据
                            selectedRowKeyList.push(currentKey);
                            selectedRowList.push(item);
                            if (isOld) {
                                handledData[index]._checked = true;
                                handledData[index]._disabled = isDisable;
                            }
                        }
                    }
                }
                if (item && typeof item === 'object' && childrenColumnName in item && Array.isArray(item[childrenColumnName])) {
                    handleDataFun(item[childrenColumnName], check, handledData[index][childrenColumnName])
                }
            })
        }

        handleDataFun(isAll ? data : showData, check, handledData)

        if (!realIsControlled) {
            setInnerSelectedRowKeys(selectedRowKeyList)
        }

        onChange && onChange(selectedRowKeyList, selectedRowList)
        onSelectAll && onSelectAll(check, selectedRowList, changeRowList)
        getSelectedDataFunc && getSelectedDataFunc(selectedRowList, undefined, undefined, handledData);
    }, [innerDisableKeys, onChange, onSelectAll, getSelectedDataFunc, innerSelectedRowKeys, realIsControlled])

    const delkey = (keys: Key[], key: Key) => {
        let index = keys.findIndex(item => item === key);
        return index > -1 ? keys.splice(index, 1) : keys
    }

    const setChildrenKey = useCallback((selectedKeyList: Key[], currentKey: Key, check: boolean) => {
        let childrenList = (dataTarget || []).filter(item => item.parentKey === currentKey); // 是否有子集
        if (!childrenList.length) return selectedKeyList; // 3
        if (check) { // 选中
            selectedKeyList = selectedKeyList.concat(childrenList.map(item => item.key)) // 3
            childrenList.forEach((item: any) => {
                selectedKeyList = setChildrenKey(selectedKeyList, item.key, check)
            })
        } else {
            childrenList.forEach((item: any) => {
                delkey(selectedKeyList, item.key)
                selectedKeyList = setChildrenKey(selectedKeyList, item.key, check)
            })
        }
        return selectedKeyList;
    }, [dataTarget])

    const setParentKey = useCallback((selectedKeyList: Key[], currentKey: Key, check: boolean) => {
        let currentTarget = (dataTarget || []).find(item => item.key === currentKey);
        let parentTarget = (dataTarget || []).find(item => item.key === currentTarget.parentKey);
        if (parentTarget) { // 存在父级
            let childrenList = (dataTarget || []).filter(item => item.parentKey === parentTarget.key);
            let isAll = true;
            for (let i = 0; i < childrenList.length; i++) {
                if (!selectedKeyList.includes(childrenList[i].key)) {
                    isAll = false;
                    continue
                }
            }
            if (check) {
                if (isAll) {
                    selectedKeyList.push(parentTarget.key);
                    setParentKey(selectedKeyList, parentTarget.key, check)
                }
            } else {
                if (!isAll) {
                    delkey(selectedKeyList, parentTarget.key)
                    setParentKey(selectedKeyList, parentTarget.key, check)
                }
            }
        }
        return selectedKeyList
    }, [dataTarget])

    const getRows = (data: DefaultRecordType[], oldData: DefaultRecordType[], selectedRows: DefaultRecordType[], selectedKeyList: Key[], innerDisableKeys: Key[], clickKey: Key, setRecord: any) => {
        data.forEach((record, index) => {
            let currentKey = getRowKey(record, index);
            if (selectedKeyList.includes(currentKey)) {
                selectedRows.push(record);
                oldData.push({
                    ...record,
                    _checked: true,
                    _disabled: innerDisableKeys.includes(currentKey)
                })
            } else {
                oldData.push({
                    ...record,
                    _checked: false,
                    _disabled: innerDisableKeys.includes(currentKey)
                })
            }
            if (currentKey === clickKey) {
                setRecord = oldData[index];
            }
            if (record[childrenColumnName] && Array.isArray(record[childrenColumnName]) && record[childrenColumnName].length) {
                oldData[index][childrenColumnName] = []
                getRows(record[childrenColumnName], oldData[index][childrenColumnName], selectedRows, selectedKeyList, innerDisableKeys, clickKey, setRecord)
            }
        });
        return {
            oldData,
            selectedRows,
            setRecord
        }
    }

    const onCheckboxChange = useCallback((record: DefaultRecordType, index: number, check: boolean, e: React.MouseEvent<HTMLElement>)=> {
        let currentKey = getRowKey(record, index);
        if (!currentKey && currentKey !== 0 && currentKey !== '0') return;
        let matchResults =
            TreeUtils.findWithPropName(data, (row: DefaultRecordType, i: number) => {
                return getRowKey(row, i);
            }, currentKey, true, true);
        let matchRecord = matchResults.length > 0 ? matchResults[0] : null;
        if (!matchRecord) return;
        // innerAutoSelect => 父子联动
        // 递归遍历
        // 1, 选中：查找某个有子集的节点，非该节点下如果他的子集全部选中，那他也选中， 该节点下，那他子集全部选中，
        // 2，取消：非该节点下，如果他的子集不是全部选中，那么他取消选中，
        //     当前点击节点下的所有子集及以下全部取消选中
        let selectedKeyList = [...innerSelectedRowKeys];
        let selectedRows: DefaultRecordType[] = [];
        let oldData: DefaultRecordType[] = [];
        let setRecord;
        if (check) {
            selectedKeyList.push(currentKey)
            if (innerAutoSelect) {
                selectedKeyList = setChildrenKey(selectedKeyList, currentKey, check);
                selectedKeyList = setParentKey(selectedKeyList, currentKey, check)
            }
        } else {
            delkey(selectedKeyList, currentKey)
            if (innerAutoSelect) {
                selectedKeyList = setChildrenKey(selectedKeyList, currentKey, check);
                selectedKeyList = setParentKey(selectedKeyList, currentKey, check)
            }
        }

        if (!realIsControlled) {
            setInnerSelectedRowKeys([...new Set(selectedKeyList)])
        }

        let getSelectedDataTarget = getRows(showData, oldData, selectedRows, [...new Set(selectedKeyList)], innerDisableKeys, currentKey, setRecord)

        onChange && onChange([...new Set(selectedKeyList)], selectedRows)
        onSelect && onSelect(matchRecord, check, selectedRows, e)
        getSelectedDataFunc && getSelectedDataFunc(getSelectedDataTarget.selectedRows, getSelectedDataTarget.setRecord, index, getSelectedDataTarget.oldData); // record需要带_checked

    }, [innerAutoSelect, onChange, onSelect, getSelectedDataFunc, innerSelectedRowKeys, realIsControlled, data]);

    const normalize = (elements: React.ReactElement[], _defaultColumns: ColumnType<DefaultRecordType>[]) => {
        const columns:ColumnType<DefaultRecordType>[] = [];
        React.Children.forEach(elements, element => {
            if (!(element && (element.type === Column || element.type === ColumnGroup))) return;
            let column = {...element.props};
            let _columnType = column.columnType || columnType.DEFAULTTYPE;
            if (element.key) {
                column.key = element.key;
            }
            if (element.type === ColumnGroup) {
                column.children = normalize(column.children, _defaultColumns);
            }
            if (_columnType === columnType.ROWDRAG) { // 拖拽行
                column = {
                    ...defaultDragHandleColumn,
                    ...column
                }
            }
            if (_columnType === columnType.MULTISELECT) { // 多选
                column = {
                    ..._defaultColumns[0],
                    ...column
                }
            }
            columns.push(column);
        });
        return columns;
    }


    const onRadioChange = useCallback((record: DefaultRecordType, index: number, event: React.MouseEvent<HTMLElement>) => {
        let currentKey = getRowKey(record, index);
        if (!currentKey && currentKey !== 0 && currentKey !== '0') return;
        let matchResults =
            TreeUtils.findWithPropName(data, (row: DefaultRecordType, i: number) => {
                return getRowKey(row, i);
            }, currentKey, true, true);
        let matchRecord = matchResults.length > 0 ? matchResults[0] : null;
        if (!matchRecord) return;
        if (innerSelectedRowKeys[0] === currentKey) {
            if (!realIsControlled) { // 非受控
                setInnerSelectedRowKeys([])
            }
            getSelectedDataFunc && getSelectedDataFunc(record, index, event);
            // antd没有单选取消事件，tinper有，且tinper受控的话需要对外返回事件
            if (!antd) {
                onSelect && onSelect(record, false, [record], event)
                onChange && onChange([], []);
            }
        } else {
            if (!realIsControlled) { // 非受控
                setInnerSelectedRowKeys([currentKey])
            }
            getSelectedDataFunc && getSelectedDataFunc(record, index, event);
            onSelect && onSelect(record, true, [record], event)
            onChange && onChange([currentKey], [record]);
        }
    }, [innerSelectedRowKeys, onSelect, onChange, getSelectedDataFunc, realIsControlled, data])

    const setByClickRowsChange = useCallback((record: DefaultRecordType, index: number, event: React.MouseEvent<HTMLElement>) => {
        let currentKey = getRowKey(record, index)
        let checked = innerSelectedRowKeys.includes(currentKey)
        if (!innerDisableKeys.includes(currentKey) && autoCheckedByClickRows) {
            if (realSelectionType === 'radio') {
                onRadioChange(record, index, event)
            } else {
                onCheckboxChange(record, index, !checked, event)
            }
        }
    }, [innerSelectedRowKeys, innerDisableKeys, autoCheckedByClickRows, realSelectionType])

    const onInvertCheckChange = useCallback(() => {
        let selectKeys = [...innerSelectedRowKeys]
        let invertDataFun = (data: DefaultRecordType[]) => {
            data.forEach((item: DefaultRecordType, index: number) => {
                let currentKey = getRowKey(item, index); // 当前key
                let isDisable = innerDisableKeys.includes(currentKey) // 是否是禁选状态
                if (!isDisable) {
                    if (selectKeys.includes(currentKey)) {
                        delkey(selectKeys, currentKey)
                    } else {
                        selectKeys = selectKeys.concat(currentKey)
                    }
                }
                if (item && typeof item === 'object' && childrenColumnName in item && Array.isArray(item[childrenColumnName])) {
                    invertDataFun(item[childrenColumnName])
                }
            })
        }
        invertDataFun(showData)
        if (!realIsControlled) {
            setInnerSelectedRowKeys(selectKeys)
        }
    }, [innerSelectedRowKeys, showData, realIsControlled])

    const mergeSelections = useMemo(() => {
        const selectionList = selections === true ? [SELECTION_ALL, SELECTION_INVERT, SELECTION_NONE] : selections;
        return selectionList;
    }, [selections])

    const onSelectionSelect = useCallback(({key}: SelectInfo) => {
        if (key === 'all') {
            // 内置全选所有的逻辑
            onAllCheckChange(true, true)
        } else if (key === 'invert') {
            // 内置反选当页的逻辑
            onInvertCheckChange()
        } else if (key === 'none') {
            // 内置清空所有的逻辑
            onAllCheckChange(false, true)
        } else {
            const _index = (mergeSelections as any[]).findIndex((el: DefaultRecordType) => el.key == key) as number
            const onSelect = (mergeSelections as any[])[_index].onSelect;
            let changedKeys = showData.flat(Infinity).map((item: DefaultRecordType, index: number) => (getRowKey(item, index))).filter((key: string) => (!innerDisableKeys.includes(key)))
            _index > -1 && onSelect && onSelect(changedKeys)
        }
    }, [mergeSelections])

    const mergeColumns = useCallback(
        (columns: ColumnType<DefaultRecordType>[]) => {
            if (realSelectionType === 'checkbox') {
                const { checkedAll, indeterminate, disabledAll } = isAllChecked;
                const menu = (
                    <Menu onClick={onSelectionSelect}>
                        {
                            ((mergeSelections || []) as MenuSelectionsType[] | string[]).map((data: MenuSelectionsType | string) => {
                                if (data === SELECTION_ALL) {
                                    return <Item key={'all'}>{'全选所有'}</Item>
                                }
                                if (data === SELECTION_INVERT) {
                                    return <Item key={'invert'}>{'反选当页'}</Item>
                                }
                                if (data === SELECTION_NONE) {
                                    return <Item key={'none'}>{'清空所有'}</Item>
                                }
                                return <Item key={(data as MenuSelectionsType).key}>{(data as MenuSelectionsType).text}</Item>
                            })
                        }
                    </Menu>
                )

                const selectionsView = <div className={`${prefix}-table-selection-extra`}>
                    <Dropdown trigger={['click']} overlay={menu} transitionName='slide-up'>
                        <Icon type="uf-anglearrowdown"/>
                    </Dropdown>
                </div>
                let defaultTitle: React.ReactNode = (
                    <Fragment>
                        <Checkbox
                            className="table-checkbox"
                            checked={checkedAll}
                            indeterminate={indeterminate}
                            {...multiSelectConfig}
                            disabled={disabledAll}
                            onChange={(checked) => onAllCheckChange(checked as boolean, false)}
                        />
                        {mergeSelections ? selectionsView : null}
                    </Fragment>
                )
                let title: React.ReactNode = columnTitle ? columnTitle : hideSelectAll ? null : defaultTitle

                let defaultColumn: ColumnType<DefaultRecordType>[] = [{
                    className: `${prefix}-table-multiSelect-column`,
                    title: title,
                    key: "checkbox",
                    dataIndex: "checkbox",
                    fixed: typeof fixed === 'boolean' ? !!fixed : 'left',
                    width: columnWidth || 40,
                    render:
                        renderCell && typeof renderCell === 'function' ?
                            (text, record, index) => renderCell(text, record, index) :
                            (_text, record, index) => {
                                let _getCheckboxProps = getCheckboxProps && typeof getCheckboxProps === 'function' ? {...getCheckboxProps(record, index)} : {}
                                let checkedRowKey = getRowKey(record, index);
                                let currentIndeterminate =
                                    record && typeof record === 'object' && childrenColumnName in record && Array.isArray(record[childrenColumnName]) && innerAutoSelect ?
                                        checkAllSelected([record], innerSelectedRowKeys, innerDisableKeys, getRowKey).indeterminate :
                                        false
                                return <Checkbox
                                    key={index}
                                    className="table-checkbox"
                                    {...multiSelectConfig}
                                    {..._getCheckboxProps}
                                    checked={innerSelectedRowKeys.includes(checkedRowKey)}
                                    disabled={innerDisableKeys.includes(checkedRowKey)}
                                    indeterminate={currentIndeterminate}
                                    onChange={(checked, event) => onCheckboxChange(record, index, checked as boolean, event as React.MouseEvent<HTMLElement>)}
                                />
                            }
                }]
                if (columns === undefined && children && Array.isArray(children)) {
                    return normalize(children, defaultColumn)
                } else {
                    return defaultColumn.concat(columns);
                }
            } else { // 单选
                let _defaultColumns: ColumnType<DefaultRecordType>[] = [{
                    className: `${prefix}-table-single-column`,
                    title: columnTitle || '',
                    key: "radio",
                    dataIndex: "radio",
                    fixed: typeof fixed === 'boolean' ? !!fixed : 'left',
                    width: columnWidth || 40,
                    render: renderCell && typeof renderCell === 'function' ? (text: any, record: DefaultRecordType, index: number) => renderCell(text, record, index) : (_text: any, record: DefaultRecordType, index: number) => {
                        let _getCheckboxProps = getCheckboxProps && typeof getCheckboxProps === 'function' ? {...getCheckboxProps(record)} : {}
                        return <RadioWrapper.Group
                            Component='div'
                            className="table-radio"
                            name="table-radio"
                            value={innerSelectedRowKeys[0]}
                            onClick={(_v: string | boolean | number, e: React.MouseEvent<HTMLElement>) => onRadioChange(record, index, e)}
                            style={{width: '14px', height: '14px', display: 'block'}}>
                            <RadioWrapper value={getRowKey(record, index)} {..._getCheckboxProps}/>
                        </RadioWrapper.Group>
                    }
                }]
                return _defaultColumns.concat(columns);
            }
        },
        [
            realSelectionType,
            showData,
            innerSelectedRowKeys,
            innerDisableKeys,
            getRowKey,
            mergeSelections,
            multiSelectConfig,
            getSelectedDataFunc,
            columns,
        ],
    );

    if (!realSelectionType) {
        return [() => columns, [], () => {}]
    }

    return [mergeColumns, innerSelectedRowKeys, setByClickRowsChange]
}

export default useSelect;